import React, { useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'

import { formatDate, getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { useTracking } from '../../../apps/professional-app/fa/common/tracking/hooks'
import { TrackEvent } from '../../../apps/professional-app/fa/common/tracking'

import { selectDocumentIsNotRelatedToClient, selectDocumentSummaryExecutionLoading } from '../../../store/processDocument/documentSummary'
import { selectDocumentFileInfo, selectSelectedDocumentType } from '../../../store/processDocument/document'
import { displayDocumentSidebar } from '../../../store/processDocument/documentSidebar'
import { selectDocumentType } from '../../../store/processDocument/processDocument'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

import { DocumentType, FALLBACK_DOCUMENT_TYPE_ID } from '../../../models/DocumentType'
import { DocumentRecord } from '../../../models/Document'
import { User } from '../../../models/User'

import { Button } from '../../../components/Button/Button'
import { ReactComponent as SyncedTaskStatus } from '../../../assets/images/task-status-synced-stroke.svg'

import { DocumentTypeSelectDropdown } from '../DocumentTypeSelectDropdown/DocumentTypeSelectDropdown'
import { DocumentSidebarTitle, DocumentSidebarType } from '../DocumentSidebar/models'
import { DeleteDocumentModal } from '../DocumentDeleteModal'
import { useDocumentLeaveBypass } from '../DocumentLeaveBypassContext'

import { DocumentSummaryContacts } from './DocumentSummaryContacts'
import { DocumentSummaryEditor } from './DocumentSummaryEditor'
import { DocumentSummaryChat } from './DocumentSummaryChat'
import { useDocumentSummary } from './hooks'

import './DocumentSummary.scss'

interface DocumentSummaryProps {
  document: DocumentRecord
  contact: User
}

export const DocumentSummary: React.FC<DocumentSummaryProps> = ({ document, contact }) => {
  const dispatch = useAppDispatch()

  const { createdAt, typeProcessing } = document
  const { label } = typeProcessing

  const { tracked } = useTracking()
  const navigate = useNavigate()

  const { bypassOnce } = useDocumentLeaveBypass()

  const [show, setShow] = useState<boolean>(false)

  const documentIsNotRelatedToClient = useAppSelector(selectDocumentIsNotRelatedToClient)
  const documentType = useAppSelector(selectDocumentType)
  const fileInfo = useAppSelector(selectDocumentFileInfo)

  const _handleOpenExternalUpdates = () => {
    dispatch(displayDocumentSidebar({ type: DocumentSidebarType.EXTERNAL_UPDATES, title: DocumentSidebarTitle.EXTERNAL_UPDATES, icon: 'bi-arrow-repeat' }))
  }

  const handleOpenExternalUpdates = tracked(_handleOpenExternalUpdates, {
    eventName: TrackEvent.EXTERNAL_UPDATES_OPENED,
    eventDetails: { documentId: document.id },
  })

  const showDeleteModal = () => {
    setShow(true)
  }

  const hideDeleteModal = () => {
    setShow(false)
  }

  const handleSuccess = () => {
    bypassOnce()
    navigate(document.documentBatchId ? `/document-batches/${document.documentBatchId}` : '/documents/upload')
  }

  return (
    <>
      {show ? <DeleteDocumentModal show={show} onHide={hideDeleteModal} onSuccess={handleSuccess} document={document} /> : null}
      <Row>
        <Col className='d-flex flex-column zocks-gap-3'>
          <Row className='d-flex align-items-center justify-content-between flex-nowrap'>
            <Col className='d-flex flex-column gap-1 mw-0'>
              <h2>
                {label} - {getUserDisplayName(contact)}
              </h2>
              <div className='d-flex align-items-center zocks-gap-0.75 paragraph-medium zocks-text-gray-700 mw-0'>
                {fileInfo ? (
                  <span className='text-truncate mw-0'>{fileInfo.fileName}</span>
                ) : (
                  <div className='d-flex align-items-center gap-1 flex-shrink-0'>
                    <i className='bi bi-file-earmark-x fs-6' />
                    <span>File deleted</span>
                  </div>
                )}
                <div className='document-summary-header-divider flex-shrink-0 zocks-bg-color-gray-700 rounded-circle' />
                {documentType ? (
                  <>
                    <span className='text-nowrap flex-shrink-0'>{documentType.name}</span>
                    <div className='document-summary-header-divider flex-shrink-0 zocks-bg-color-gray-700 rounded-circle' />
                  </>
                ) : null}
                <span className='text-nowrap flex-shrink-0'>{formatDate(createdAt)}</span>
              </div>
            </Col>
            <Col className='d-flex align-items-center gap-3' xs='auto'>
              <DocumentTypeSelectDropdown disabled={!fileInfo} />
              <Button variant='header' className='zocks-p-0.625' onClick={handleOpenExternalUpdates}>
                <SyncedTaskStatus width={18} height={18} stroke='#000' />
              </Button>
              <div className='document-summary-header-icon-divider flex-shrink-0 align-self-stretch zocks-bg-color-gray-300' />
              <Button variant='header' className='zocks-p-0.625' onClick={showDeleteModal}>
                <i className='bi bi-trash fs-6' />
              </Button>
            </Col>
          </Row>
          <Row>
            <Col xs={7} className='d-flex flex-column gap-3'>
              <DocumentTypeChangedBadge documentType={documentType} />
              <DocumentSummaryEditor />
            </Col>
            <Col xs={5} className='d-flex flex-column gap-4'>
              {documentIsNotRelatedToClient ? <DocumentIsNotRelatedToClientBadge contact={contact} /> : null}
              <DocumentSummaryContacts document={document} contact={contact} />
              <DocumentSummaryChat />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
}

interface DocumentIsNotRelatedToClientBadgeProps {
  contact: User
}

const DocumentIsNotRelatedToClientBadge: React.FC<DocumentIsNotRelatedToClientBadgeProps> = ({ contact }) => {
  return (
    <div className='d-flex align-items-center gap-3 p-3 zocks-bg-color-warning rounded-3'>
      <i className='bi bi-exclamation-triangle fs-5' />
      <span className='paragraph-medium'>
        This document may not be related to {getUserDisplayName(contact)}. Review the summary and insights before syncing.
      </span>
    </div>
  )
}

interface DocumentTypeChangedBadgeProps {
  documentType: DocumentType | null
}

const DocumentTypeChangedBadge: React.FC<DocumentTypeChangedBadgeProps> = ({ documentType }) => {
  const executionLoading = useAppSelector(selectDocumentSummaryExecutionLoading)
  const selectedDocumentType = useAppSelector(selectSelectedDocumentType)
  const fileInfo = useAppSelector(selectDocumentFileInfo)

  const { handleRerun } = useDocumentSummary()

  if (!documentType || !selectedDocumentType || !fileInfo) {
    return null
  }

  if (documentType.systemDocumentTypeId === FALLBACK_DOCUMENT_TYPE_ID) {
    return (
      <div className='d-flex flex-column gap-3 p-3 zocks-bg-color-primary-light rounded-3'>
        <div className='d-flex gap-3'>
          <i className='bi bi-exclamation-circle fs-5' />
          <div className='d-flex flex-column gap-1'>
            <span className='paragraph-medium bold'>Get better results with a specific Document Type</span>
            <span className='paragraph-medium'>
              Your AI Assistant used a generic template because it couldn't find a match. Choose a document type below and rerun, or create a{' '}
              <Link to='/workspace/setup/document-type/document-type/create' target='_blank' className='zocks-text-primary text-decoration-underline'>
                custom Document Type
              </Link>
              .
            </span>
          </div>
        </div>
        <div className='d-flex align-items-center gap-3 zocks-ps-2.25'>
          <div className='document-summary-type-selector-container'>
            <DocumentTypeSelectDropdown />
          </div>
          <Button variant='primary' className='py-2 zocks-px-0.75' disabled={!selectedDocumentType} onClick={handleRerun} loading={executionLoading}>
            Rerun
          </Button>
        </div>
      </div>
    )
  }

  if (documentType.id !== selectedDocumentType.id) {
    return (
      <div className='d-flex align-items-center gap-3 p-3 zocks-bg-color-primary-light rounded-3'>
        <i className='bi bi-exclamation-circle fs-5' />
        <span className='paragraph-medium'>The document type has changed. Rerun processing to apply the new template.</span>
        <Button variant='primary' className='px-2 zocks-py-0.25' onClick={handleRerun} loading={executionLoading}>
          Rerun
        </Button>
      </div>
    )
  }

  return null
}
